import type { $Fetch, FetchOptions } from 'ofetch';
import {
  ACCESS_TOKEN,
  getNewTokenRequestBody,
} from '~/models/AccessCookies.model';

/**
 * A TypeScript type for error information related to Halebop request errors that we throw
 * for every failed request (except 401) in `factory.ts`.
 * This type should be updated if additional fields are returned from the backend.
 *
 * Checking a catch-error with `isNuxtError<HalebopRequestErrorData>(error)` will return true
 * simply if the error is a NuxtError, which it is when it's thrown in `factory.ts` with `createError()`.
 *
 * `isNuxtError<HalebopRequestErrorData>(error)` will still return true even if the error was thrown with
 * `createError<SomethingElse>()`, but with this type we can infer the types for the `error.data` object
 * without having to cast to `any` etc.
 */
export type HalebopRequestErrorData = {
  messages?: string[];
  errors?: { [key: string]: string };
  errorCodeDetail?: string;
  detail?: string;
  title?: string;
};

/**
 * The FetchFactory acts as a wrapper around an HTTP client.
 * It encapsulates the functionality for making API requests asynchronously
 * through the call function, utilizing the provided HTTP client.
 */
class FetchFactory<T> {
  private $fetch: $Fetch;
  private _apiBaseUrl: string = '';

  constructor(fetcher: $Fetch, fetchOptions?: FetchOptions) {
    this.$fetch = fetcher;
    this._apiBaseUrl = fetchOptions?.baseURL ?? '';
  }

  async refreshToken() {}

  /**
   * The HTTP client is utilized to control the process of making API requests.
   * @param method the HTTP method (GET, POST, ...)
   * @param url the endpoint url
   * @param data the body data
   * @param fetchOptions fetch options
   * @returns
   */
  async call(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    data?: object,
    fetchOptions?: FetchOptions<'json'>,
  ): Promise<T> {
    const tokenCookie = useCookie(ACCESS_TOKEN);
    const apiBaseUrl = this._apiBaseUrl;

    return await this.$fetch<T>(url, {
      method,
      body: data,
      ...fetchOptions,
      ...{
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${tokenCookie?.value ?? ''}`,
          ...fetchOptions?.headers,
          'cache-control': 'no-cache',
        },
      },
      retry: 1,
      retryStatusCodes: [401],
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      async onResponseError({ response, options, error }) {
        if (response.status == 401) {
          const newTokenReq = await $fetch<{ token: string }>(
            `${apiBaseUrl}/api-frontend/Authenticate/GetToken`,
            {
              method: 'post',
              body: getNewTokenRequestBody,
            },
          );
          tokenCookie.value = newTokenReq.token;
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${newTokenReq.token}`,
          } as Headers;
        } else {
          let errorMessage = undefined;
          let errorName = '';
          let data: object = {};

          if (response?._data) {
            data = { ...data, ...response?._data };
          }

          if (response?._data?.message) {
            errorMessage = response?._data?.message;
          }

          if (response?.statusText) {
            errorName = response.statusText;
          }

          throw createError<HalebopRequestErrorData>({
            statusCode: response.status,
            message: errorMessage,
            data: data !== undefined ? data : undefined,
            name: errorName,
          });
        }
      },
    });
  }

  async post(
    url: string,
    data: object,
    fetchOptions?: FetchOptions<'json'>,
  ): Promise<T> {
    return this.call('POST', url, data, fetchOptions);
  }

  async get(url: string, fetchOptions?: FetchOptions<'json'>): Promise<T> {
    return this.call('GET', url, undefined, fetchOptions);
  }

  async put(
    url: string,
    data: object,
    fetchOptions?: FetchOptions<'json'>,
  ): Promise<T> {
    return this.call('PUT', url, data, fetchOptions);
  }
}

export default FetchFactory;
